<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { TeamType, TeamStatus } from '@/types'
import type { Team } from '@/types'

const route = useRoute()
const router = useRouter()
const incidentId = route.params.id as string

const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()

const fieldTeams = computed(() =>
  teamsStore.teams.filter((t) => t.team_type !== TeamType.Overhead),
)

const teamTypeLabel: Record<TeamType, string> = {
  [TeamType.Ground]: 'Ground',
  [TeamType.K9]: 'K9',
  [TeamType.Air]: 'Air',
  [TeamType.Bike]: 'Bike',
  [TeamType.Type1]: 'Type 1',
  [TeamType.Rescue]: 'Rescue',
  [TeamType.Overhead]: 'Overhead',
}

function teamTypeBadgeClass(type: TeamType): string {
  const map: Partial<Record<TeamType, string>> = {
    [TeamType.Ground]: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800',
    [TeamType.K9]: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
    [TeamType.Air]: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800',
    [TeamType.Bike]: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950 dark:text-teal-300 dark:border-teal-800',
    [TeamType.Type1]: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800',
    [TeamType.Rescue]: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800',
  }
  return (
    (map[type] ?? 'bg-surface-container text-on-surface-variant border-border') +
    ' border text-[10px] font-label uppercase tracking-wide px-2 py-0.5 rounded'
  )
}

function teamStatusPipClass(status: TeamStatus): string {
  const map: Record<TeamStatus, string> = {
    [TeamStatus.Emergency]: 'bg-red-500',
    [TeamStatus.OnScene]: 'bg-green-500',
    [TeamStatus.Assigned]: 'bg-green-400',
    [TeamStatus.Enroute]: 'bg-blue-500',
    [TeamStatus.RTB]: 'bg-amber-500',
    [TeamStatus.Returned]: 'bg-outline-variant',
    [TeamStatus.AwaitingAssignment]: 'bg-outline-variant',
    [TeamStatus.Building]: 'bg-outline-variant',
  }
  return map[status] ?? 'bg-outline-variant'
}

function teamStatusTextClass(status: TeamStatus): string {
  const map: Record<TeamStatus, string> = {
    [TeamStatus.Emergency]: 'text-red-600',
    [TeamStatus.OnScene]: 'text-green-600',
    [TeamStatus.Assigned]: 'text-green-600',
    [TeamStatus.Enroute]: 'text-blue-600',
    [TeamStatus.RTB]: 'text-amber-600',
    [TeamStatus.Returned]: 'text-outline',
    [TeamStatus.AwaitingAssignment]: 'text-outline',
    [TeamStatus.Building]: 'text-outline',
  }
  return map[status] ?? 'text-outline'
}

function getTeamLeaderName(team: Team): string {
  if (!team.team_leader_personnel_id) return '—'
  const p = personnelStore.roster.find((r) => r.personnel_id === team.team_leader_personnel_id)
  return p ? `${p.first} ${p.last}` : '—'
}

function getTeamMemberCount(teamId: string): number {
  return teamsStore.getTeamMembers(teamId).filter((m) => m.active).length
}

function go() {
  router.push({ name: 'incident-teams', params: { id: incidentId } })
}
</script>

<template>
  <div class="bg-surface border border-border rounded-lg p-4 flex flex-col h-[280px]">
    <div class="flex items-center justify-between mb-3 shrink-0">
      <span class="text-[10px] font-label uppercase tracking-widest text-outline">Teams on scene</span>
      <button
        class="p-0 text-[11px] text-primary font-label uppercase tracking-wide hover:underline"
        @click="go"
      >
        View teams →
      </button>
    </div>

    <div v-if="fieldTeams.length" class="flex flex-col gap-1.5 overflow-y-auto flex-1 min-h-0">
      <div
        v-for="team in fieldTeams"
        :key="team.team_id"
        class="flex items-center gap-3 px-3 py-2.5 bg-background border border-border rounded hover:border-on-surface/20 cursor-pointer"
        @click="go"
      >
        <span :class="teamTypeBadgeClass(team.team_type)">
          {{ teamTypeLabel[team.team_type] }}
        </span>

        <div class="flex-1 min-w-0">
          <span class="text-[13px] text-on-surface font-medium">{{ team.team_identifier }}</span>
          <span class="text-[11px] text-outline ml-2">{{ getTeamLeaderName(team) }}</span>
        </div>

        <span class="text-[10px] font-label uppercase tracking-wide text-outline shrink-0">
          {{ getTeamMemberCount(team.team_id) }} mbr
        </span>

        <div class="flex items-center gap-1.5 shrink-0">
          <span :class="['w-1.5 h-1.5 rounded-full shrink-0', teamStatusPipClass(team.team_status)]" />
          <span :class="['text-[11px] font-label', teamStatusTextClass(team.team_status)]">
            {{ team.team_status }}
          </span>
        </div>
      </div>
    </div>

    <p v-else class="text-sm text-outline italic">No teams created yet.</p>
  </div>
</template>
